import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { GET_SETTINGS } from '../graphql/query';
import { UPDATE_SETTINGS } from '../graphql/mutation';
import { AppSettings, SettingsData, UpdateSettingsInput, BillingMode } from '../types/interfaces';
import { PageHeader } from '../components/common';

const validationSchema = yup.object({
  companyName: yup.string().required('Company name is required'),
  address: yup.string().required('Address is required'),
  phone: yup.string().required('Phone is required'),
  abn: yup.string().required('ABN is required'),
  defaultHourlyRate: yup.number().min(0, 'Must be zero or greater').required('Required'),
  defaultDailyRate: yup.number().min(0, 'Must be zero or greater').required('Required'),
  defaultTaxRate: yup.number().min(0, 'Must be between 0 and 1').max(1, 'Must be between 0 and 1').required('Required'),
  paymentTermsDays: yup.number().integer('Must be a whole number').min(0, 'Must be zero or greater').required('Required'),
  invoiceNumberPrefix: yup.string().trim().matches(/^[^-]+$/, 'Prefix cannot contain a hyphen').required('Prefix is required'),
  emailFromName: yup.string().required('From name is required'),
  emailReplyTo: yup
    .string()
    .nullable()
    .test('email-or-empty', 'Must be a valid email', (v) => !v || yup.string().email().isValidSync(v)),
  defaultBillingMode: yup.string().oneOf(['HOURLY', 'FIXED']).required(),
});

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
    {children}
  </Typography>
);

const emptyValues: UpdateSettingsInput = {
  companyName: '', address: '', phone: '', abn: '',
  defaultHourlyRate: 0, defaultDailyRate: 0, defaultTaxRate: 0, paymentTermsDays: 0,
  invoiceNumberPrefix: 'INV', emailFromName: '', emailReplyTo: '', defaultBillingMode: 'HOURLY',
};

const toInput = (s: AppSettings): UpdateSettingsInput => ({
  companyName: s.companyName,
  address: s.address,
  phone: s.phone,
  abn: s.abn,
  defaultHourlyRate: s.defaultHourlyRate,
  defaultDailyRate: s.defaultDailyRate,
  defaultTaxRate: s.defaultTaxRate,
  paymentTermsDays: s.paymentTermsDays,
  invoiceNumberPrefix: s.invoiceNumberPrefix,
  emailFromName: s.emailFromName,
  emailReplyTo: s.emailReplyTo,
  defaultBillingMode: s.defaultBillingMode,
});

const SettingsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading, error } = useQuery<SettingsData>(GET_SETTINGS);
  const [updateSettings, { loading: saving }] = useMutation(UPDATE_SETTINGS);

  const formik = useFormik<UpdateSettingsInput>({
    enableReinitialize: true,
    initialValues: data?.settings ? toInput(data.settings) : emptyValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        await updateSettings({
          variables: { input: { ...values, defaultBillingMode: values.defaultBillingMode as BillingMode } },
          refetchQueries: [{ query: GET_SETTINGS }],
        });
        enqueueSnackbar('Settings saved', { variant: 'success' });
      } catch (e) {
        enqueueSnackbar(e instanceof Error ? e.message : 'Failed to save settings', { variant: 'error' });
      }
    },
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <PageHeader title="Settings" />
        <Typography color="error">Failed to load settings: {error.message}</Typography>
      </Box>
    );
  }

  const numberField = (name: keyof UpdateSettingsInput, label: string, step = 'any') => (
    <TextField
      fullWidth
      type="number"
      inputProps={{ step }}
      label={label}
      name={name}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] && formik.errors[name]}
    />
  );

  const textField = (name: keyof UpdateSettingsInput, label: string) => (
    <TextField
      fullWidth
      label={label}
      name={name}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] && formik.errors[name]}
    />
  );

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <PageHeader
        title="Settings"
        subtitle="Business configuration used when generating invoices"
        action={
          <Button
            type="submit"
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={saving}
          >
            Save changes
          </Button>
        }
      />

      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <SectionLabel>Company profile</SectionLabel>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>{textField('companyName', 'Company name')}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{textField('abn', 'ABN')}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{textField('phone', 'Phone')}</Grid>
          <Grid size={{ xs: 12 }}>{textField('address', 'Address')}</Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <SectionLabel>Rates &amp; billing</SectionLabel>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>{numberField('defaultHourlyRate', 'Default hourly rate ($)')}</Grid>
          <Grid size={{ xs: 12, sm: 4 }}>{numberField('defaultDailyRate', 'Default daily rate ($)')}</Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              select
              label="Default billing mode"
              name="defaultBillingMode"
              value={formik.values.defaultBillingMode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <MenuItem value="HOURLY">Hourly</MenuItem>
              <MenuItem value="FIXED">Fixed price</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <SectionLabel>Tax &amp; payment terms</SectionLabel>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>{numberField('defaultTaxRate', 'Default tax rate (0–1, e.g. 0.10)')}</Grid>
          <Grid size={{ xs: 12, sm: 6 }}>{numberField('paymentTermsDays', 'Payment terms (days)', '1')}</Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Tax rate is editable here but is not yet applied to invoice totals.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <SectionLabel>Invoice numbering &amp; email</SectionLabel>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>{textField('invoiceNumberPrefix', 'Invoice number prefix')}</Grid>
          <Grid size={{ xs: 12, sm: 4 }}>{textField('emailFromName', 'Email from name')}</Grid>
          <Grid size={{ xs: 12, sm: 4 }}>{textField('emailReplyTo', 'Email reply-to')}</Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">
        Last updated: {data?.settings ? new Date(data.settings.updatedAt).toLocaleString() : '—'}
      </Typography>
    </Box>
  );
};

export default SettingsPage;
